import { watch, type WatchStopHandle } from 'vue';
import { loadErpDefinitions, setErpDefinitionTextSink } from '@erp/connect/shell';
import { isAuthenticated } from '@/auth';
import { appI18n } from '@/i18n/appI18n';

/**
 * Shell side of the ERP's screen definitions (BLP/nx#3257). The ERP serves its
 * forms, route table, menu and texts over the API; `@erp/connect/shell` loads
 * them. Connect owns i18n, so it registers the sink that merges the served texts
 * into the ERP's own message table (I-2284) — German whole, English only the
 * menu and cockpit, the policy sits with the ERP's sink.
 */

/** Registers the sink once at shell start-up; the texts arrive when the load finishes. */
export function registerErpDefinitionTexts(): void {
  setErpDefinitionTextSink((lang, messages) => {
    appI18n('erp').global.mergeLocaleMessage(lang, messages);
  });
}

/**
 * Starts the definitions load for the command palette before the ERP is opened:
 * as soon as the ERP permission snapshot is in for an authenticated session, and
 * again after every re-login (the snapshot drops on logout and loads anew). Safe
 * to repeat — a loaded store answers at once, a failed load is retried here and
 * by the loader on the next navigation into the ERP.
 */
export function watchErpDefinitionsLoading(
  permissionsLoaded: () => boolean,
  authenticated: () => boolean = isAuthenticated,
): WatchStopHandle {
  return watch(
    permissionsLoaded,
    (loaded) => {
      if (loaded && authenticated()) void loadErpDefinitions().catch(() => {});
    },
    { immediate: true },
  );
}
